// PrefsProvider — global container for everything locale/currency related:
//   • language (synced both ways with i18n + /api/auth/me when logged in)
//   • display_currency (USD canonical → display via Intl.NumberFormat)
//   • default_country (used by DealWizard Step 1)
//   • FX rates (fetched once from /api/fx/rates, cached for the session)
//
// All other components should read these via `usePrefs()` instead of reaching
// into useAuth().user directly.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import i18n, { SUPPORTED_LANGUAGES } from "@/i18n";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PrefsContext = createContext(null);

export function PrefsProvider({ children }) {
  const { user, refresh } = useAuth();
  const [language, setLanguageState] = useState(i18n.language || "en");
  const [displayCurrency, setDisplayCurrencyState] = useState(
    () => localStorage.getItem("runidea.currency") || "USD"
  );
  const [defaultCountry, setDefaultCountryState] = useState(
    () => localStorage.getItem("runidea.country") || "US"
  );
  const [fxRates, setFxRates] = useState(null);

  // Pull FX rates once on mount (no auth required).
  useEffect(() => {
    let alive = true;
    api
      .get("/fx/rates")
      .then(({ data }) => {
        if (alive) setFxRates(data?.rates || null);
      })
      .catch((err) => {
        // Non-fatal — formatters fall back to 1:1 conversion. Surface in dev.
        if (process.env.NODE_ENV !== "production") {
          console.warn("PrefsContext: /fx/rates fetch failed", err);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  // When the user object loads, server prefs take precedence over local guesses.
  useEffect(() => {
    if (!user) return;
    if (user.language && SUPPORTED_LANGUAGES.some((l) => l.code === user.language)) {
      if (user.language !== i18n.language) i18n.changeLanguage(user.language);
      setLanguageState(user.language);
    }
    if (user.display_currency) {
      setDisplayCurrencyState(user.display_currency);
      localStorage.setItem("runidea.currency", user.display_currency);
    }
    if (user.default_country) {
      setDefaultCountryState(user.default_country);
      localStorage.setItem("runidea.country", user.default_country);
    }
  }, [user]);

  // Keep our state in sync if i18n changes from elsewhere (e.g. browser detection).
  useEffect(() => {
    const onChange = (lng) => setLanguageState(lng);
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);

  const setLanguage = useCallback(
    async (code) => {
      if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
      await i18n.changeLanguage(code);
      setLanguageState(code);
      if (user) {
        try {
          await api.patch("/auth/me", { language: code });
          await refresh();
        } catch (err) {
          // Non-fatal — UI already switched locally. Persistence will retry next time.
          if (process.env.NODE_ENV !== "production") {
            console.warn("PrefsContext.setLanguage persist failed", err);
          }
        }
      }
    },
    [user, refresh]
  );

  const setDisplayCurrency = useCallback(
    async (cc) => {
      const upper = (cc || "USD").toUpperCase();
      setDisplayCurrencyState(upper);
      localStorage.setItem("runidea.currency", upper);
      if (user) {
        try {
          await api.patch("/auth/me", { display_currency: upper });
          await refresh();
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("PrefsContext.setDisplayCurrency persist failed", err);
          }
        }
      }
    },
    [user, refresh]
  );

  const setDefaultCountry = useCallback(
    async (cc) => {
      const upper = (cc || "US").toUpperCase();
      setDefaultCountryState(upper);
      localStorage.setItem("runidea.country", upper);
      if (user) {
        try {
          await api.patch("/auth/me", { default_country: upper });
          await refresh();
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("PrefsContext.setDefaultCountry persist failed", err);
          }
        }
      }
    },
    [user, refresh]
  );

  const value = useMemo(
    () => ({
      language,
      displayCurrency,
      // Phase 7 — `homeCurrency` is the canonical alias. Components should
      // prefer this; `displayCurrency` is kept for backward compatibility.
      homeCurrency: displayCurrency,
      setHomeCurrency: setDisplayCurrency,
      defaultCountry,
      fxRates,
      setLanguage,
      setDisplayCurrency,
      setDefaultCountry,
      isRtl: i18n.dir() === "rtl",
    }),
    [language, displayCurrency, defaultCountry, fxRates, setLanguage, setDisplayCurrency, setDefaultCountry]
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs() {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs must be used inside <PrefsProvider>");
  return ctx;
}
